import { useEffect, useState } from 'react';
import { Crown, X, Calendar, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useI18n } from '../i18n';

export default function SubscriptionCard() {
    const { user, refresh } = useAuth();
    const { locale } = useI18n();
    const ownsPlus = !!(user?.premium && user?.premium_via_plus);
    const da = locale === 'da';
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);

    useEffect(() => {
        let active = true;
        setSub(null);
        setConfirm(false);
        if (!user?.premium || ownsPlus) { setLoading(false); return; }
        setLoading(true);
        api.get('/paypal/subscription').then((r) => { if (active) setSub(r.data); })
            .catch(() => { if (active) setSub(null); }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [user?.premium, user?.user_id, ownsPlus]);

    if (!user?.premium) return null;
    if (ownsPlus) return <section className="mb-6 bg-brand-card border border-brand-ochre/30 rounded-3xl p-5" data-testid="plus-subscription-card">
        <h2 className="font-serif text-xl text-brand-ink inline-flex items-center gap-2"><Crown size={18} /> Premium Plus · Google Play</h2>
        <p className="text-sm text-brand-ink2 mt-2">{da
            ? 'Premium Plus-adgang er aktiv. Se næste betaling, fornyelsesstatus eller opsig abonnementet i Google Play. AI-puljen er begrænset og fornyes månedligt; det er ikke en ny betalingsdato.'
            : 'Premium Plus access is active. Check your next payment, renewal status or cancel in Google Play. The AI allowance is limited and resets monthly; that is not a new billing date.'}</p>
        <a className="inline-block underline text-brand-green mt-3" href="https://play.google.com/store/account/subscriptions?package=fun.growdaily.app">{da ? 'Administrer i Google Play' : 'Manage in Google Play'}</a>
    </section>;
    if (loading) return (
        <section className="mb-6">
            <div className="bg-brand-card border border-brand-border rounded-3xl p-5 flex items-center gap-2 text-brand-ink3 text-sm">
                <Loader2 size={14} className="animate-spin" /> Loading subscription…
            </div>
        </section>
    );

    const renewDate = sub?.next_billing_time ? new Date(sub.next_billing_time).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : null;
    const isTrial = sub?.status === 'APPROVAL_PENDING' || sub?.in_trial;
    const isCancelled = sub?.status === 'CANCELLED' || sub?.status === 'EXPIRED' || !sub;
    const billing = sub?.billing || 'monthly';

    const cancel = async () => {
        setBusy(true);
        try {
            await api.post('/paypal/cancel');
            toast.success("Subscription cancelled. You'll keep access until the end of your billing period.");
            setConfirm(false);
            await refresh();
            const r = await api.get('/paypal/subscription').catch(() => ({ data: null }));
            setSub(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not cancel — try managing via PayPal directly.');
        } finally { setBusy(false); }
    };

    return (
        <section className="mb-6" data-testid="subscription-card">
            <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                <Crown size={11} /> Subscription
            </h2>
            <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isCancelled ? 'bg-brand-bg2 text-brand-ink3' : 'bg-brand-ochre/20 text-brand-ochre'}`}>
                        {isCancelled ? <X size={18} /> : <CheckCircle2 size={18} />}
                    </div>
                    <div className="flex-1">
                        <div className="font-serif text-base text-brand-ink leading-tight">
                            GrowDaily Premium {sub && <>· <span className="capitalize">{billing}</span></>}
                        </div>
                        <div className="text-xs text-brand-ink3 mt-1">
                            {!sub ? 'No PayPal subscription details are available here. This does not mean your Premium access is cancelled. Manage the subscription with the provider you purchased from.' : isCancelled
                                ? 'Cancelled — your data remains, you can resubscribe anytime.'
                                : isTrial
                                ? 'In free trial. You can cancel anytime before the trial ends to avoid being charged.'
                                : 'Active. Cancel anytime — you keep access until the next billing date.'}
                        </div>
                        {renewDate && !isCancelled && (
                            <div className="mt-2 text-[11px] inline-flex items-center gap-1 text-brand-ink2 bg-brand-bg2 px-2 py-1 rounded-full">
                                <Calendar size={11} /> Next billing: {renewDate}
                            </div>
                        )}
                    </div>
                </div>

                {!isCancelled && !confirm && (
                    <button
                        data-testid="btn-cancel-subscription"
                        onClick={() => setConfirm(true)}
                        className="w-full py-2.5 rounded-full text-sm border border-brand-terracotta/30 text-brand-terracotta hover:bg-brand-terracotta/10"
                    >
                        Cancel subscription
                    </button>
                )}

                {confirm && (
                    <div className="bg-brand-terracotta/5 border border-brand-terracotta/30 rounded-2xl p-3 mt-2">
                        <p className="text-xs text-brand-ink2 mb-3">
                            Are you sure? You'll keep the benefits of this subscription until {renewDate || 'the end of your billing period'}. Cancelling does not delete your garden or cancel a separate subscription with another provider.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setConfirm(false)}
                                disabled={busy}
                                className="py-2 rounded-full text-xs bg-brand-bg2 border border-brand-border text-brand-ink hover:bg-brand-bg"
                            >
                                Keep Premium
                            </button>
                            <button
                                data-testid="btn-confirm-cancel"
                                onClick={cancel}
                                disabled={busy}
                                className="py-2 rounded-full text-xs bg-brand-terracotta text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : 'Confirm cancel'}
                            </button>
                        </div>
                    </div>
                )}

                {sub ? <a
                    href="https://www.paypal.com/myaccount/autopay/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="link-manage-paypal"
                    className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] text-brand-ink3 hover:text-brand-ink2 py-1"
                >
                    Manage payment method on PayPal <ExternalLink size={10} />
                </a> : <div className="mt-3 space-y-2 text-xs text-brand-ink3" data-testid="subscription-provider-help">
                    <p>Payment-provider details are unavailable here. This does not cancel or change your Premium access.</p>
                    <a className="inline-block underline" href="https://play.google.com/store/account/subscriptions?package=fun.growdaily.app">Manage Google Play subscription</a>
                    <span className="mx-1">·</span>
                    <a className="underline" href="https://www.paypal.com/myaccount/autopay/">Check PayPal subscriptions</a>
                </div>}
            </div>
        </section>
    );
}
